import { auth } from './firebase';
import { callFunction, FunctionsError, RequestCancelledError } from './functions';
import { logGlobalError } from '../utils/globalErrorLogger';

/**
 * Message-board handle provisioning.
 *
 * The `assignMessageBoardUserName` cloud function resolves its subject from the
 * auth token, so it can only ever provision the *caller's own* account. That
 * single constraint shapes everything here: an admin cannot mint a handle for
 * somebody else, so a user who slips through without one stays stuck — unable
 * to post, and unable to be added to any channel — until they themselves come
 * back and ask for one. `ensureMessageBoardUserNameForSession` is that ask.
 */

interface AssignUserNameResponse {
  success?: boolean;
  /** The deployed function returns `userName`; `username` is accepted defensively. */
  userName?: string;
  username?: string;
  alreadyAssigned?: boolean;
}

/**
 * Provisions a handle for the signed-in caller and returns it. Idempotent
 * server-side: an account that already has one gets it back unchanged. Throws
 * on transport failure — callers decide whether that is fatal.
 */
export async function assignMessageBoardUserName(): Promise<string | null> {
  const result = await callFunction<AssignUserNameResponse>('assignMessageBoardUserName');
  const userName = result?.userName ?? result?.username;
  return typeof userName === 'string' && userName.length > 0 ? userName : null;
}

export class MessageBoardUserNameConflictError extends Error {}

/** Use the original server generator without assigning or reserving a username. */
export async function previewMessageBoardUserName(excludeUserName: string): Promise<string> {
  const result = await callFunction<AssignUserNameResponse>('previewMessageBoardUserName', { excludeUserName });
  if (result?.success !== true || typeof result.userName !== 'string' || !result.userName || result.userName === excludeUserName) {
    throw new Error('Unable to generate a new username. Please try again.');
  }
  return result.userName;
}

/** Save the preview and wait for existing message/member labels to be updated. */
export async function renameMessageBoardUserName(userName: string, expectedUserName: string): Promise<void> {
  try {
    const result = await callFunction<AssignUserNameResponse>(
      'renameMessageBoardUserName',
      { userName, expectedUserName },
      { timeoutMs: 600_000 },
    );
    if (result?.success !== true || result.userName !== userName) {
      throw new Error('The username update was not confirmed. Please retry Save.');
    }
  } catch (error) {
    if (error instanceof FunctionsError && error.status === 409) {
      const body = error.body;
      throw new MessageBoardUserNameConflictError(
        typeof body === 'object' && body !== null && 'error' in body && typeof body.error === 'string'
          ? body.error : 'Your username could not be updated. Cancel and reopen Identity to try again.',
      );
    }
    throw error;
  }
}

/** The account fields the backstop needs — kept structural so this module
 *  stays free of the wider `UserAccount` surface. */
export interface MessageBoardUserNameSubject {
  id: string;
  firebaseId: string;
  messageBoardUserName?: string;
}

const checkedUserAccountIds = new Set<string>();

/** Test seam: clears the once-per-session guard. */
export function resetMessageBoardUserNameSessionGuard(): void {
  checkedUserAccountIds.clear();
}

/**
 * Backstop for the handle that onboarding is supposed to provision.
 *
 * Onboarding's message-board step is best-effort and can fail transiently,
 * which used to leave a user without a handle indefinitely. This runs at most
 * once per account per page session: if the handle is missing, it asks the
 * cloud function for one. Best-effort by design — a failure here must never
 * interfere with signing in, so it resolves rather than throws.
 *
 * Deliberately stricter than aligned's `_ensureMessageBoardUserName`, which
 * clears its guard on failure. Here the caller is a live Firestore
 * subscription that re-fires on every account-document change, so clearing the
 * guard would let a persistent server-side failure retry in a loop. One
 * attempt per page load is enough: `callFunction` already retries transient
 * failures internally, and the next sign-in starts a fresh session.
 */
export async function ensureMessageBoardUserNameForSession(
  account: MessageBoardUserNameSubject,
): Promise<string | null> {
  if (checkedUserAccountIds.has(account.id)) return null;
  if (account.messageBoardUserName) return account.messageBoardUserName;
  if (!account.firebaseId) return null;

  // Never provision against somebody else's account. The function would
  // resolve the *caller* from their token, so firing it while the signed-in
  // user differs from `account` (a sign-out race, or an admin impersonating)
  // would write a handle onto the wrong user. Leave the guard clear so a
  // later snapshot, once auth has settled, can still heal this account.
  if (auth.currentUser?.uid !== account.firebaseId) return null;

  checkedUserAccountIds.add(account.id);
  try {
    return await assignMessageBoardUserName();
  } catch (err) {
    if (err instanceof RequestCancelledError) {
      // The browser cancelled the request (page teardown/navigation), not a
      // provisioning failure — let a later session retry instead of logging it.
      checkedUserAccountIds.delete(account.id);
      return null;
    }
    logGlobalError(err, {
      context: 'ensureMessageBoardUserNameForSession',
      metadata: { userAccountId: account.id },
    });
    return null;
  }
}
